import React from 'react';
import styled from 'styled-components';
import { THEME_COLORS } from '../common/colors';

const Title = styled.h1`
  margin-bottom: 8px;
  text-align: center;
`;

const Summary = styled.p`
  margin-bottom: 8px;
  text-align: left;
  line-height: 24px;
`;

const Subtitle = styled.h3`
  margin: 8px;
`;

const PageContainer = styled.div`
  max-width: 700px;
  margin: 0 auto;
  padding: 36px;
  background-color: #fff;
`;

const AgreementList = styled.ol`
  margin: 20px;
  padding-left: 8px;
  li {
    margin-bottom: 8px;
    line-height: 24px;
  }
`;

export function TermsAndConditions() {
  return (
    <div style={{ backgroundColor: THEME_COLORS.backgroundDecorator }}>
      <PageContainer>
        <Title>WeSuap's Terms & Conditions of Service</Title>
        <Summary>
          <b>Effective Date: 01.01.2025. </b> <br />
          Welcome to WeSuap. These Terms of Service (“Terms”) are a legally
          binding agreement between you (“you,” “user”) and WeSuap Inc.
          (“WeSuap,” “we,” “us,” or “our”) governing your access to and use of
          our websites, applications, and services (the “Platform”). Please read
          these Terms carefully. By accessing or using WeSuap, you agree to be
          bound by them. If you do not agree, please do not use the Platform.
        </Summary>
        <AgreementList>
          <li>
            <b>Who Can Use WeSuap:</b> <br />
            You must:
            <br />
            <ul>
              <li>Be at least 18 years old</li>
              <li>Have legal capacity to enter a binding agreement</li>
              <li>Use WeSuap for lawful and respectful purposes</li>
            </ul>
            We may restrict or terminate access if we reasonably believe you
            violated these Terms, harmed the community, or put the Platform or
            others at risk.
          </li>
          <li>
            <b>What WeSuap Is (And Isn't):</b>
            <br />
            WeSuap is a platform that helps people connect, exchange skills or
            services, and reach goals in non-monetary ways. <br />
            <b>
              WeSuap does not vet, endorse, or guarantee any user, match, skill
              item, or outcome.
            </b>
            We are a facilitator of connections - not a broker, employer, agent,
            or guarantor.
            <br />
            <b>You are solely responsible</b> for your decisions and
            interactions on the platform.
          </li>
          <li>
            <b>User Responsibilities:</b>
            <br />
            You agree to:
            <br />
            <ul>
              <li>Provide accurate, truthful information</li>
              <li>Not impersonate others or misrepresent your offerings</li>
              <li>Use the platform with integrity and respect</li>
              <li>Comply with all applicable laws and these Terms</li>
            </ul>
            WeSuap may suspend or permanently ban users who engage in abusive,
            exploitative, or illegal behavior.
          </li>
          <li>
            <b>AI Features:</b>
            <br />
            WeSuap uses AI to:
            <br />
            <ul>
              <li>Suggest matches and prompts</li>
              <li>Facilitate introductions or group chats</li>
              <li>Help guide your skill-sharing journey</li>
            </ul>
            By using WeSuap, you consent to AI interactions and acknowledge
            that:
            <br />
            <ul>
              <li>AI is supportive, not perfect or authoritative</li>
              <li>
                AI recommendations are not professional, legal, medical, or
                financial advice
              </li>
              <li>You are always in control of who you interact with</li>
            </ul>
          </li>
          <li>
            <b>Your Content:</b> <br />
            You retain full ownership of anything you post, including offers,
            items, skills, goals, or messages. By using WeSuap, you grant us a{' '}
            <b>non-exclusive, worldwide, royalty-free license</b> to use,
            display, adapt, and distribute your content{' '}
            <b>
              only for the purpose of operating and improving the Platform.{' '}
            </b>
            We reserve the right to remove or restrict content that violates
            these Terms or harms others.
          </li>
          <li>
            <b>Payments, Points, and Exchanges:</b> <br />
            WeSuap may offer optional paid features (e.g., memberships, point
            packs, transaction fees). By purchasing anything on WeSuap, you
            agree to:
            <br />
            <ul>
              <li>Provide valid payment information</li>
              <li>Pay all applicable charges and fees</li>
              <li>Not attempt to reverse or fraudulently dispute charges</li>
            </ul>
            We do not offer refunds unless required by law.
            <br />
            You understand WeSuap is not responsible for what happens in
            exchanges between users - including the delivery, quality, or
            fairness of any trade or transaction.
          </li>
          <li>
            <b>Platform Disclaimers (Very Important):</b> <br />
            <ul>
              <li>
                <b>WeSuap is provided "as is" and "as available."</b>
              </li>
              <li>
                We make no warranties or guarantees about matches, content,
                reliability, safety, or success.
              </li>
              <li>
                You assume full responsibility for your use of the platform.
              </li>
              <li>
                We <b>do not screen</b> users, their items, or services.
              </li>
            </ul>
            <b>You use WeSuap entirely at your own risk.</b> Please exercise
            caution and judgement.
          </li>
          <li>
            <b>Limitation of Liability:</b>
            <br /> To the fullest extent permitted by law:
            <br />
            <ul>
              <li>
                <b>WeSuap is not liable</b> for any indirect, incidental,
                punitive, or consequential damages - including loss of data,
                reputation, revenue, or opportunity
              </li>
              <li>
                Our <b>total liability to you is limited to $100</b> or the
                amount you paid us in the last 12 months - whichever is less
              </li>
              <li>
                <b>You agree not to hold WeSuap responsible</b> for any
                interactions, exchanges, or disputes with other users
              </li>
            </ul>
          </li>
          <li>
            <b>Indemnity:</b>
            <br /> You agree to defend, indemnify, and hold harmless WeSuap and
            its affiliates, officers, employees, and contractors from any
            claims, damages, or legal fees arising from: <br />
            <ul>
              <li>Your use of the Platform</li>
              <li>Your interactions with others</li>
              <li>Your breach of these Terms</li>
              <li>
                Any content you submit or actions you take while using WeSuap
              </li>
            </ul>
          </li>
          <li>
            <b>Termination:</b> <br />
            We may suspend or terminate your access to WeSuap at any time, with
            or without notice, if we believe you've violated these Terms or
            endangered others. <b />
            You may stop using the Platform or delete your account at any time.
            Certain provisions (e.g., indemnity, limitation of liability,
            ownership, dispute resolution) survive termination.
          </li>
          <li>
            <b>Intellectual Property:</b> <br />
            WeSuap's brand, logo, technology, and content are owned by WeSuap
            Inc. or licensed to us. You may not copy, reverse-engineer, resell,
            or misuse the Platform or any of its components without written
            permission.
          </li>
          <li>
            <b>Privacy and Data Use:</b> <br />
            Your use of WeSuap is also governed by our [Privacy Policy], which
            explains how we collect, use, and safeguard your information. <br />
            We follow global standards including <b>GDPR, CCPA,</b> and other
            privacy laws where applicable. You consent to our data practices by
            using the Platform.
          </li>
          <li>
            <b>Governing Law & Dispute Resolution</b> <br />
            These Terms are governed by the laws of the{' '}
            <b>State of Delaware, USA.</b> <br />
            Any dispute must first be attempted to be resolved informally. If
            that fails, disputes will be resolved through{' '}
            <b>binding arbitration in Delaware.</b> You waive your right to
            class actions or jury trials to the extent allowed by law.
          </li>
          <li>
            <b>Updates to These Terms: </b> <br />
            We may update these Terms from time to time. If we make material
            changes, we will notify you via email, banner, or app notification.
            Continued use of WeSuap after changes means you accept the updated
            Terms.
          </li>
          <li>
            <b>Contact Us: </b> <br />
            Questions? Concerns? We're here to help. <br />
            Email: <b>hello@WeSuap.com</b> <br />
          </li>
        </AgreementList>
        <br />
        <Subtitle>
          Communications & Messaging (SMS, Notifications, and Alerts)
        </Subtitle>
        <Summary>
          By creating an account or providing your phone number, you consent to
          receive communications from WeSuap via SMS, email, in-app
          notifications, or other messaging channels.
        </Summary>
        <b>Transactional Messages</b>
        <br />
        <Summary>
          Transactional messages are sent automatically in response to user
          actions or for security and service purposes. These may include:
        </Summary>
        <ul>
          <li>One-time passcodes (OTP) for authentication</li>
          <li>
            Notifications about offers, matches, exchanges, or account activity
          </li>
          <li>Security alerts or important service notices</li>
        </ul>
        <Summary>
          Transactional messages are essential to the operation of the Platform.
          Message frequency varies. Message and data rates may apply.
        </Summary>
        <b>Marketing Messages (Optional)</b>
        <br />
        <Summary>
          If you explicitly opt in, WeSuap may send marketing or discovery
          messages, including notifications about new offers, features, or
          opportunities that may be relevant to you. You may opt out of
          marketing messages at any time by replying STOP to any marketing SMS.
          Opting out of marketing messages does not affect your receipt of
          transactional or security messages.
        </Summary>
        <b>Help & Support</b>
        <br />
        <ul>
          <li>Reply HELP to any SMS for assistance</li>
          <li>Contact us at hello@wesuap.com</li>
        </ul>
        <b>User Responsibilities</b>
        <br />
        <Summary>You represent and warrant that:</Summary>
        <ul>
          <li>
            You are the owner or authorized user of the phone number you provide
          </li>
          <li>You consent to receive messages at that number</li>
          <li>
            You will not use WeSuap to send spam, fraudulent, abusive, or
            unlawful communications
          </li>
        </ul>
        <b>Third-Party Messaging Providers</b>
        <br />
        <Summary>
          WeSuap uses third-party service providers, including Twilio, to
          deliver communications. Message data may be processed by these
          providers in accordance with their policies and applicable law.
        </Summary>
        <b>Legal Compliance</b>
        <br />
        <Summary>
          All messaging conducted through WeSuap must comply with applicable
          laws and regulations, including but not limited to the TCPA, CTIA
          guidelines, and international messaging and data protection laws where
          applicable.
        </Summary>
        <b>Final Word</b> <br />
        WeSuap exists to build mutual value through human connection. These
        Terms are designed to protect that mission — and protect you, the user —
        as we build trust at scale. <br />
      </PageContainer>
    </div>
  );
}
